import type { PartnerRuleCreate, PartnerRuleSet, PartnerRuleSetUpdate } from '@shipium-corp/shipium-ts-types';
import GatewayAxiosClient from '~/store/services/GatewayAxiosClient';
import { rules } from './rules';

export const ruleSets = {
  partnerRuleSet: {
    queryKey: 'partner-management-service/rule-sets/partner',
    configurationType: 'default' as const,
    fn: async (populateRules = true) => {
      const { data } = await GatewayAxiosClient.get<PartnerRuleSet>(
        `/partner-management-proxy/api/v1/partners/ruleSets/partnerRuleSet?populateRules=${populateRules}`,
      );

      return data;
    },
  },
  get: {
    queryKey: 'partner-management-service/rule-sets/id',
    fn: async (ruleSetId: string) => {
      const { data } = await GatewayAxiosClient.get<PartnerRuleSet>(
        `/partner-management-proxy/api/v1/partners/ruleSets/${ruleSetId}`,
      );

      return data;
    },
  },
  update: async ({ ruleSetId, payload }: { ruleSetId: string; payload: PartnerRuleSetUpdate }) => {
    const { data } = await GatewayAxiosClient.put<PartnerRuleSet>(
      `/partner-management-proxy/api/v1/partners/ruleSets/${ruleSetId}`,
      payload,
    );

    return {
      data,
      invalidateQueryKeys: [
        ['partner-management-service/rule-sets/partner'],
        ['partner-management-service/rule-sets/id', ruleSetId],
      ],
    };
  },
  createAndAddRule: async ({ ruleSetId, rule }: { ruleSetId: string; rule: PartnerRuleCreate }) => {
    const [createdRule, ruleSet] = await Promise.all([rules.create(rule), ruleSets.get.fn(ruleSetId)]);
    const lastRule = ruleSet.rules[ruleSet.rules.length - 1];

    const ruleSetUpdate = await ruleSets.update({
      ruleSetId,
      payload: {
        ...ruleSet,
        rules: [
          ...ruleSet.rules,
          {
            ruleId: createdRule.data.ruleId,
            sequence: (lastRule?.sequence ?? 0) + 1,
          },
        ],
      },
    });

    return { invalidateQueryKeys: [...createdRule.invalidateQueryKeys, ...ruleSetUpdate.invalidateQueryKeys] };
  },
  archiveRule: async ({ ruleId, ruleSetId }: { ruleId: string; ruleSetId: string }) => {
    const rule = await rules.get.fn(ruleId);
    const ruleSet = await ruleSets.get.fn(ruleSetId);

    const [ruleUpdate, ruleSetUpdate] = await Promise.all([
      rules.update({
        ruleId,
        payload: rules.buildUpdatePayloadFromRule({ ...rule, active: false, archived: true }),
      }),
      ruleSets.update({
        ruleSetId,
        payload: {
          ...ruleSet,
          rules: ruleSet.rules.filter((currentRule) => currentRule.ruleId !== ruleId),
        },
      }),
    ]);

    return {
      invalidateQueryKeys: [...ruleUpdate.invalidateQueryKeys, ...ruleSetUpdate.invalidateQueryKeys],
    };
  },
  restoreRule: async ({ ruleId, ruleSetId }: { ruleId: string; ruleSetId: string }) => {
    const [rulePayload, ruleSetPayload] = await Promise.all([rules.get.fn(ruleId), ruleSets.get.fn(ruleSetId)]);
    const lastRule = ruleSetPayload.rules[ruleSetPayload.rules.length - 1];

    const [ruleUpdate, ruleSetUpdate] = await Promise.all([
      rules.update({
        ruleId,
        payload: rules.buildUpdatePayloadFromRule({
          ...rulePayload,
          active: false,
          archived: false,
        }),
      }),
      ruleSets.update({
        ruleSetId: ruleSetId,
        payload: {
          ...ruleSetPayload,
          rules: [...ruleSetPayload.rules, { ruleId, sequence: (lastRule?.sequence ?? 0) + 1 }],
        },
      }),
    ]);

    return {
      invalidateQueryKeys: [...ruleUpdate.invalidateQueryKeys, ...ruleSetUpdate.invalidateQueryKeys],
    };
  },
  updateRulePriority: async ({
    ruleId,
    ruleSetId,
    priorityAmount,
  }: {
    ruleId: string;
    ruleSetId: string;
    priorityAmount: number;
  }) => {
    const payload = await ruleSets.get.fn(ruleSetId);
    const indexOfValue = payload.rules.findIndex((currentRule) => currentRule.ruleId === ruleId);

    const temp = payload.rules[indexOfValue].sequence;
    payload.rules[indexOfValue].sequence = payload.rules[indexOfValue - priorityAmount].sequence ?? 0;
    payload.rules[indexOfValue - priorityAmount].sequence = temp;

    return ruleSets.update({ ruleSetId, payload });
  },
};
